'use client'

import {useEffect, useState} from 'react'
import {Moon, Sun} from 'lucide-react'
import {applyTheme} from '@/lib/theme'

/**
 * One-tap light/dark switch. Flips whatever is showing now — an explicit
 * choice or the device's — and remembers it like Settings does. The
 * three-way choice (incl. "match my device") stays in Settings.
 *
 * Takes its labels as props so the landing page, which has no shell text,
 * can use it too; `ThemeToggle` is the app-shell flavour.
 */
export function ThemeSwitch({
  toLight,
  toDark,
  className = 'u-app__avatar'
}: {
  toLight: string
  toDark: string
  className?: string
}) {
  // Unknown until mounted: the server can't see the device preference.
  const [dark, setDark] = useState<boolean | null>(null)

  useEffect(() => {
    const explicit = document.documentElement.dataset.theme
    setDark(explicit ? explicit === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches)
  }, [])

  return (
    <button
      type="button"
      className={className}
      aria-label={dark ? toLight : toDark}
      onClick={() => {
        const next = !dark
        setDark(next)
        applyTheme(next ? 'dark' : 'light')
      }}
    >
      {dark ? <Sun aria-hidden="true" size={18} /> : <Moon aria-hidden="true" size={18} />}
    </button>
  )
}
